import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useHistory } from "react-router-dom";
import MUIDataTable from "mui-datatables";
import {
  useLazyGetClusteringQuery,
  useLazyGetMinutesIssueQuery,
} from "../../../services/minute";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  AppBar,
  Toolbar,
  Typography,
  Tooltip,
  TextField,
  Button,
  Stack,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ClustersTable from "./ClustersTable";
import { MinuteIssuesTable } from "./MinuteIssuesTable";
import type { MinutesIssues } from "../../../services/minute";

const ClusterPage = () => {
  const location = useLocation();
  const [keyword, setKeyword] = useState(
    new URLSearchParams(location.search).get("keyword") || ""
  );
  const [company_name, setCompanyName] = useState(
    new URLSearchParams(location.search).get("company_name") || ""
  );
  const [triggerGetClustering, { data, error, isLoading }] =
    useLazyGetClusteringQuery();

  const [
    triggerGetMinutesIssue,
    {
      data: minutesData,
      isFetching: isMinutesFetching,
      isError: isMinutesError,
      error: minutesError,
      fulfilledTimeStamp: minutesFulfilledTs,
    },
  ] = useLazyGetMinutesIssueQuery();

  const [isKeywordChanged, setIsKeywordChanged] = useState(false);
  const [reLoading, setReLoading] = useState(false); // 検索中の状態を管理するための状態
  const [popupOpen, setPopupOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [clusterFilter, setClusterFilter] = useState(0.7);
  const [issueFilter, setIssueFilter] = useState(0.7);

  useEffect(() => {
    const fetchData = async (retryCount = 0) => {
      try {
        const response = await triggerGetClustering({
          keyword,
          cluster_filter: clusterFilter,
          issue_filter: issueFilter,
        });
        if (response.error) {
          const error = response.error as any;
          if (error.status === "FETCH_ERROR" && retryCount < 2) {
            console.log(`再試行中... (${retryCount + 1}回目)`);
            fetchData(retryCount + 1); // すぐに再試行
          } else if (error.status === "FETCH_ERROR") {
            setPopupOpen(true);
            setErrorMessage(
              "サーバータイムアウト:検索フィルターを上げるかもう一度実行してください"
            );
            setIsKeywordChanged(true);
          } else {
            setPopupOpen(true);
            setErrorMessage("サーバーエラー");
            setIsKeywordChanged(true);
          }
        } else if (response.data && response.data.clusters.length === 0) {
          setPopupOpen(true);
          setErrorMessage("検索フィルターの値を下げてください");
          setIsKeywordChanged(true);
        }
      } catch (error) {
        setPopupOpen(true);
        setErrorMessage("予期せぬエラーが発生しました");
        console.log("予期せぬエラーが発生しました:", error);
      }
    };

    if (keyword) {
      fetchData();
    }
  }, [triggerGetClustering]);

  const dataSource = data?.clusters[0]?.data_source || "不明";

  // ニュース記事から類似課題検索
  const history = useHistory();
  const handleReExecute = (retryCount = 0) => {
    setIsKeywordChanged(false);
    setReLoading(true); // 検索中の状態にする
    const url = `/clusters?keyword=${encodeURIComponent(
      keyword
    )}&company_name=${encodeURIComponent(
      company_name
    )}&cluster_filter=${clusterFilter}&issue_filter=${issueFilter}`;
    history.push(url);
    triggerGetClustering({
      keyword,
      cluster_filter: clusterFilter,
      issue_filter: issueFilter,
    })
      .then((response) => {
        console.log("ニュース記事検索実行:", response);
        if (response.error) {
          const error = response.error as any;
          if (error.status === "FETCH_ERROR" && retryCount < 2) {
            console.log(`再試行中... (${retryCount + 1}回目)`);
            handleReExecute(retryCount + 1); // すぐに再試行
          } else if (error.status === "FETCH_ERROR") {
            setPopupOpen(true);
            setErrorMessage(
              "サーバータイムアウト:検索フィルターを上げるかもう一度実行してください"
            );
            setIsKeywordChanged(true);
            setReLoading(false);
          } else {
            setPopupOpen(true);
            setErrorMessage("サーバーエラー");
            setIsKeywordChanged(true);
            setReLoading(false);
          }
        } else if (response.data && response.data.clusters.length === 0) {
          setPopupOpen(true);
          setErrorMessage("検索フィルターの値を下げてください");
          setReLoading(false); // データがゼロ件で状態を解除
          setIsKeywordChanged(true);
          setReLoading(false);
        } else {
          setReLoading(false); // データ取得が成功した場合に状態を解除
        }
      })
      .catch((error) => {
        setPopupOpen(true);
        setErrorMessage("予期せぬエラーが発生しました");
        console.log("予期せぬエラーが発生しました:", error);
        setReLoading(false); // 予期せぬエラーを受けたときに状態を解除
      });
  };

  // 商談メモから類似課題検索
  const handleReExecute2 = (retryCount = 0) => {
    console.log("商談メモ検索実行:");
    setIsKeywordChanged(false);
    setReLoading(true); // 検索中の状態にする
    const url = `/clusters?keyword=${encodeURIComponent(
      keyword
    )}&company_name=${encodeURIComponent(
      company_name
    )}&cluster_filter=${clusterFilter}&issue_filter=${issueFilter}`;
    history.push(url);
    triggerGetMinutesIssue({
      keyword,
      issue_filter: 0,
    })
      .then((response) => {
        if (response.error) {
          const error = response.error as any;
          if (error.status === "FETCH_ERROR" && retryCount < 2) {
            console.log(`再試行中... (${retryCount + 1}回目)`);
            handleReExecute2(retryCount + 1); // すぐに再試行
          } else if (error.status === "FETCH_ERROR") {
            setPopupOpen(true);
            setErrorMessage(
              "サーバータイムアウト:検索フィルターのスコアを上げるかもう一度実行してください"
            );
            setIsKeywordChanged(true);
            setReLoading(false);
          } else {
            setPopupOpen(true);
            setErrorMessage("サーバーエラー");
            setIsKeywordChanged(true);
            setReLoading(false);
          }
        } else if (response.data && response.data.results.length === 0) {
          setPopupOpen(true);
          setErrorMessage("検索フィルターのスコア値を下げてください");
          setReLoading(false); // データがゼロ件で状態を解除
          setIsKeywordChanged(true);
          setReLoading(false);
        } else {
          setReLoading(false); // データ取得が成功した場合に状態を解除
        }
      })
      .catch((error) => {
        setPopupOpen(true);
        setErrorMessage("予期せぬエラーが発生しました");
        console.log("予期せぬエラーが発生しました:", error);
        setReLoading(false); // 予期せぬエラーを受けたときに状態を解除
      });
  };

  // パラメーター 'e' に型を指定
  const handleKeywordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(e.target.value);
    setIsKeywordChanged(true);
  };

  // 商談メモ課題表示用
  const minuteRows: MinutesIssues[] = useMemo(
    () => minutesData?.results ?? [],
    [minutesData]
  );

  return (
    <div>
      {(isLoading || reLoading) && (
        <Box
          sx={{
            position: "absolute",
            top: "60%",
            right: "50%",
            zIndex: 1300, // MUIのDialogと同等のz-index
          }}
        >
          <CircularProgress />
        </Box>
      )}

      <AppBar position="static" sx={{ mb: 2 }}>
        <Toolbar>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ flexGrow: 1 }}
          >
            <Typography variant="subtitle1" sx={{ ml: 2 }}>
              課題：
            </Typography>
            <TextField
              value={keyword}
              onChange={handleKeywordChange}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  if (
                    !isLoading &&
                    !reLoading &&
                    isKeywordChanged &&
                    keyword.trim() !== ""
                  ) {
                    handleReExecute();
                  }
                }
              }}
              size="small"
              variant="outlined"
              multiline
              minRows={2}
              maxRows={2}
              sx={{
                width: "25%",
                backgroundColor: "white",
                ml: 2,
                // ★ v5: textarea を CSS で直接ターゲット
                "& textarea": {
                  resize: "none",
                },
                // （必要なら）input クラスも併用
                "& .MuiInputBase-inputMultiline": {
                  resize: "none",
                },
              }}

              // 背景色を白に設定し、余白を追加
            />
            <Button
              variant="contained"
              onClick={() => handleReExecute()}
              disabled={
                !isKeywordChanged ||
                keyword.trim() === "" ||
                isLoading ||
                reLoading
              }
              sx={{
                backgroundColor: "#4caf50",
                color: "white",
                ml: 2, // 余白を追加
                "&:hover": {
                  backgroundColor: "#388e3c", // ホバー時の色を変更
                },
              }}
            >
              ニュース記事から検索
            </Button>
            <Button
              variant="contained"
              onClick={() => handleReExecute2()}
              disabled={
                !isKeywordChanged ||
                keyword.trim() === "" ||
                isLoading ||
                reLoading
              }
              sx={{
                backgroundColor: "#4caf50",
                color: "white",
                ml: 2, // 余白を追加
                "&:hover": {
                  backgroundColor: "#388e3c", // ホバー時の色を変更
                },
              }}
            >
              商談メモから検索
            </Button>
          </Stack>
        </Toolbar>
      </AppBar>
      <Box sx={{ display: "flex", flexDirection: "column", mb: 2 }}>
        {/* 上の行 */}
        <h3>類似課題検索（データソース: {dataSource}）</h3>
        {/* 下の行 */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ flex: 5, display: "flex", justifyContent: "center" }}>
            <TextField
              variant="filled"
              label="元の課題"
              value={
                new URLSearchParams(location.search).get("keyword") ||
                "課題が選択されていません"
              }
              multiline
              InputProps={{
                readOnly: true,
              }}
              minRows={2}
              maxRows={2}
              sx={{
                width: "80%",
                "& textarea": { resize: "none" },
                "& .MuiInputBase-inputMultiline": { resize: "none" },
              }}
            />
          </Box>
          <Box sx={{ flex: 3, display: "flex", justifyContent: "center" }}>
            <TextField
              variant="outlined"
              label="企業名"
              value={company_name}
              multiline
              onChange={(e) => setCompanyName(e.target.value)}
              minRows={2}
              maxRows={2}
              sx={{
                width: "80%",
                "& textarea": { resize: "none" },
                "& .MuiInputBase-inputMultiline": { resize: "none" },
              }}
            />
          </Box>
          <Box
            sx={{
              flex: 2,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Typography variant="h6">検索フィルター</Typography>
              <Tooltip
                title={
                  <div>
                    <p>グループスコア/スコアの基準</p>
                    <p>0.95以上：非常に高い類似度。ほぼ同じ意味合いの課題。</p>
                    <p>0.85～0.95：高い類似度。かなりの共通点がある課題。</p>
                    <p>
                      0.70～0.85：中程度の類似度。ある程度似ているが、相違点も含む課題。
                    </p>
                    <p>
                      0.50～0.70：低い類似度。異なる内容だが、いくつかの共通点がある課題。
                    </p>
                    <p>
                      0.30～0.50：非常に低い類似度。関連性が少なく、わずかな共通点がある課題。
                    </p>
                    <p>
                      0.30未満：ほぼ無関係。関連性をほとんど見出せない課題。
                    </p>
                  </div>
                }
              >
                <HelpOutlineIcon
                  style={{
                    marginLeft: "8px",
                    cursor: "pointer",
                    color: "blue",
                  }}
                />
              </Tooltip>
            </Box>
            <FormControl sx={{ width: "80%", mb: 2 }}>
              <InputLabel>グループスコア</InputLabel>
              <Select
                value={clusterFilter}
                onChange={(e) => {
                  setClusterFilter(Number(e.target.value));
                  setIsKeywordChanged(true);
                }}
                label="グループスコア"
              >
                {[...Array(11)].map((_, i) => (
                  <MenuItem key={i} value={i / 10}>
                    {i / 10}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ width: "80%" }}>
              <InputLabel>スコア</InputLabel>
              <Select
                value={issueFilter}
                onChange={(e) => {
                  setIssueFilter(Number(e.target.value));
                  setIsKeywordChanged(true);
                }}
                label="スコア"
              >
                {[...Array(11)].map((_, i) => (
                  <MenuItem key={i} value={i / 10}>
                    {i / 10}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>
      {(isLoading || reLoading) && (
        <Box sx={{ width: "100%" }}>
          <LinearProgress />
        </Box>
      )}
      {!isLoading && !data && <p>データがありません</p>}
      {data && (
        <TableContainer component={Paper}>
          <h3 style={{ margin: "12px 0" }}>ニュース記事の類似課題一覧</h3>
          <Table aria-label="collapsible table">
            <TableHead>
              <TableRow sx={{ display: "flex" }}>
                <TableCell style={{ flex: 1 }}>
                  <strong>No</strong>
                </TableCell>
                <TableCell style={{ flex: 1 }}></TableCell>
                <TableCell style={{ flex: 6 }}>
                  <strong>グループ名</strong>
                </TableCell>
                <TableCell style={{ flex: 2 }}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <strong>グループスコア</strong>
                    <Tooltip
                      title={
                        <div>
                          <p>グループスコアの基準</p>
                          <p>
                            0.95以上：非常に高い類似度。ほぼ同じ意味合いの課題。
                          </p>
                          <p>
                            0.85～0.95：高い類似度。かなりの共通点がある課題。
                          </p>
                          <p>
                            0.70～0.85：中程度の類似度。ある程度似ているが、相違点も含む課題。
                          </p>
                          <p>
                            0.50～0.70：低い類似度。異なる内容だが、いくつかの共通点がある課題。
                          </p>
                          <p>
                            0.30～0.50：非常に低い類似度。関連性が少なく、わずかな共通点がある課題。
                          </p>
                          <p>
                            0.30未満：ほぼ無関係。関連性をほとんど見出せない課題。
                          </p>
                        </div>
                      }
                    >
                      <HelpOutlineIcon
                        style={{
                          marginLeft: "8px",
                          cursor: "pointer",
                          color: "blue",
                        }}
                      />
                    </Tooltip>
                  </div>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.clusters.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    一致する課題は見つかりませんでした
                  </TableCell>
                </TableRow>
              ) : (
                data.clusters
                  .slice()
                  .sort((a, b) => b.cluster_score - a.cluster_score)
                  .map((clusters, index) => (
                    <ClustersTable
                      key={clusters.cluster_id}
                      row={{ ...clusters, issues: clusters.issues || [] }}
                      index={index}
                    />
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* 商談メモ課題テーブル表示 */}
      {!isMinutesFetching && minuteRows.length > 0 && (
        <div
          style={{
            marginTop: 16,
            overflow: "auto",
            contain: "layout",
            maxHeight: 480,
          }}
        >
          <h3 style={{ margin: "12px 0" }}>商談メモの類似課題一覧</h3>
          <MinuteIssuesTable rows={minuteRows} />
        </div>
      )}

      <Snackbar
        open={popupOpen}
        autoHideDuration={5000}
        onClose={() => setPopupOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }} // 画面中央に表示
      >
        <Alert
          onClose={() => setPopupOpen(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ClusterPage;
